import { toast } from 'sonner'
import useMutationCreateBrokeFlexMessageCampaign from '@/modules/messaging/api/hooks/useMutationCreateBrokeFlexMessageCampaign'
import useMutationUpdateBrokeFlexMessageCampaign from '@/modules/messaging/api/hooks/useMutationUpdateBrokeFlexMessageCampaign'
import useMutationPreviewBrokeFlexMessageStage from '@/modules/messaging/api/hooks/useMutationPreviewBrokeFlexMessageStage'
import type { CampaignFormValues } from '@/modules/messaging/pages/CreateBrokeFlexMessagePage/types'
import type { BrokeFlexMessageCampaignModel } from '@/modules/messaging/types'

interface UseCampaignActionsProps {
	mode: 'create' | 'edit'
	campaignId?: string
	initialData?: BrokeFlexMessageCampaignModel
	onSuccess: () => void
}

export function useCampaignActions({
	mode,
	campaignId,
	initialData,
	onSuccess,
}: UseCampaignActionsProps) {
	const createMutation = useMutationCreateBrokeFlexMessageCampaign()
	const updateMutation = useMutationUpdateBrokeFlexMessageCampaign()
	const previewMutation = useMutationPreviewBrokeFlexMessageStage()

	const handlePreview = async (stageId: string) => {
		if (!campaignId) {
			toast.error('ต้องบันทึกแคมเปญก่อนดูตัวอย่าง')
			return
		}

		try {
			await previewMutation.mutateAsync({ campaignId, stageId })
			toast.success('ส่งข้อความตัวอย่างสำเร็จ กรุณาเช็คใน LINE')
		} catch (error) {
			console.error('Preview error:', error)
			toast.error('ไม่สามารถส่งข้อความตัวอย่างได้')
		}
	}

	const handleSubmit = async (data: CampaignFormValues) => {
		try {
			const payload = {
				name: mode === 'create' ? `${data.name} (C)` : data.name, // Add (C) suffix for custom campaigns
				isActive: mode === 'create' ? false : (initialData?.isActive ?? true), // Create as inactive
				stages: data.stages.map((stage, idx) => ({
					minutesAfterTrigger: stage.minutesAfterTrigger,
					message: stage.message,
					maxItemsToShow: stage.maxItemsToShow,
					order: idx,
					items: stage.items.map((item, itemIdx) => ({
						type: item.type,
						order: itemIdx,
						eligibilityType: item.eligibilityType,
						minValue: item.minValue,
						displayName: item.displayName,
						icon: item.icon,
						description: item.description,
						message: item.message,
						imageUrl: item.imageUrl,
						promotionId: item.promotionId,
						couponCode: item.couponCode,
						actionUrl: item.actionUrl,
						enabled: item.enabled,
					})),
				})),
			}

			if (mode === 'edit' && campaignId) {
				await updateMutation.mutateAsync({
					id: campaignId,
					data: payload,
				})
				toast.success('อัปเดตแคมเปญสำเร็จ')
			} else {
				await createMutation.mutateAsync(payload)
				toast.success('สร้างแคมเปญสำเร็จ')
			}

			onSuccess()
		} catch (error) {
			console.error('Error saving campaign:', error)
			toast.error(
				mode === 'edit'
					? 'เกิดข้อผิดพลาดในการอัปเดตแคมเปญ'
					: 'เกิดข้อผิดพลาดในการสร้างแคมเปญ',
			)
		}
	}

	const isPending = createMutation.isPending || updateMutation.isPending

	return {
		handlePreview,
		handleSubmit,
		isPending,
	}
}
